export const RENEWAL_WINDOW_DAYS = 60;

export type RenewalDisplay = {
  kind: "due" | "overdue";
  text: string;
};

type RenewalRow = { endDate?: string | Date | null };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const atMidnight = (value: Date) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();

// endDate denotes a calendar date, not an instant: the backend reads a Postgres
// `date` column through a transformer that yields midnight on its UTC server, so
// it arrives either as a full ISO instant or as a bare "YYYY-MM-DD". Read the
// date part literally in both shapes -- running the instant through local time
// would land a day early west of Greenwich.
const parseDate = (value: string | Date): Date => {
  if (value instanceof Date) {
    return value;
  }

  const [year, month, day] = value.slice(0, 10).split("-").map(Number);

  return new Date(year, month - 1, day);
};

export const getRenewalDisplay = (
  row: RenewalRow,
  now: Date = new Date()
): RenewalDisplay | null => {
  if (!row.endDate) {
    return null;
  }

  const days = Math.round((atMidnight(parseDate(row.endDate)) - atMidnight(now)) / MS_PER_DAY);

  if (days < 0) {
    return { kind: "overdue", text: "Renewal overdue" };
  }

  if (days > RENEWAL_WINDOW_DAYS) {
    return null;
  }

  if (days === 0) {
    return { kind: "due", text: "Renews today" };
  }

  return { kind: "due", text: `Renews in ${days} day${days === 1 ? "" : "s"}` };
};

export const countContractsDueForRenewal = (
  rows: RenewalRow[] | undefined,
  now: Date = new Date()
): number => (rows ?? []).filter((row) => getRenewalDisplay(row, now) !== null).length;
